import { featureFlags } from '@seen/core/featureFlags'
import type { Editor } from '@tiptap/core'
import { type JSX, useMemo, useSyncExternalStore } from 'react'
import {
  AI_PROPOSAL_INSERT_CLASS,
  AI_PROPOSALS_PLUGIN_KEY,
  type AIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  type FillReplacement,
  splitFillReplacement,
} from '#src/components/rich-text-editor/fillReplacement.ts'
import {
  type IslandActions,
  IslandActionsProvider,
  useIslandActions,
} from '#src/components/rich-text-editor/IslandActionsContext.tsx'
import { IslandEditor } from '#src/components/rich-text-editor/IslandEditor.tsx'
import type { IslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import { suggestionContentHtml } from '#src/components/rich-text-editor/suggestionContentExtensions.ts'
import { SuggestionContentPreview } from '#src/components/rich-text-editor/SuggestionContentPreview.tsx'
import { SuggestionSourceExcerpt } from '#src/components/rich-text-editor/SuggestionSourceExcerpt.tsx'
import { RenderSafeContext } from '#src/contexts/RenderSafeContext.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export interface SuggestionWidgetProps {
  proposal: AIProposal
  registry: IslandRegistry | null
  /* The host editor, whose text a fill-only replace is split against. Without
   * one the whole replacement shows, as for any replace. */
  editor?: Editor
}

const noopSubscribe = (): (() => void) => () => {}

/* The component the widget shell renders for a pending suggestion (design §2.a).
 * Default state is the read-only SuggestionContentPreview — unchanged behavior.
 * Clicking that preview opens this proposal's island at the clicked position:
 * the shell's registry flips it open and this swaps to the editable
 * IslandEditor, forwarding the click's mapped caret position. The swap
 * keys off the island registry (external store), NOT ProseMirror plugin state:
 * an island is local and never enters the shared doc, so its open state is not
 * document state. The shell caches/reuses one instance of this per proposal id,
 * so the preview↔island transition preserves the shell-lifecycle invariants. */
export function SuggestionWidget({
  proposal,
  registry,
  editor,
}: SuggestionWidgetProps): JSX.Element {
  /* The campaign gate for every island interaction, applied at the one place a
   * click can reach the registry. Off, the widget drops back to what it was
   * before the island existed: a read-only preview with no click-to-edit, no
   * island mount, and therefore no island edit/dismiss behavior. The flag is
   * read here rather than threaded because this component is mounted by the
   * host editor's ReactRenderer, not by a React parent that could pass it — the
   * same reason IslandEditor reads it directly. The decoration, the staged
   * suggestions banner, and the Basic-resource persistence are all upstream of
   * this and stay live either way. */
  const noteSuggestionsEnabled = useFeatureFlag(featureFlags.aiNoteSuggestions)
  const islandRegistry = noteSuggestionsEnabled ? registry : null
  const editing = useSyncExternalStore(
    islandRegistry ? islandRegistry.subscribe : noopSubscribe,
    () => islandRegistry?.getOpenSnapshot(proposal.id) ?? false
  )

  const fill = useFillReplacement(editor, proposal)
  const hostActions = useIslandActions()

  /* A fill-only replace shows and edits only what it adds: the card previews
   * the addition, and the island opens on it. Whatever leaves the island — its
   * live content read by a plain Accept, a kept edit, an Accept with edits — is
   * put back into the whole replacement first, so the range is still written
   * whole. */
  const island = useMemo(() => {
    if (!(fill && islandRegistry)) {
      return null
    }
    const { original, effective } = fill
    const registry: IslandRegistry = {
      ...islandRegistry,
      registerController: (id, controller) =>
        islandRegistry.registerController(id, {
          ...controller,
          getCurrentContent: () =>
            effective.compose(controller.getCurrentContent()),
        }),
    }
    const actions: IslandActions | null = hostActions && {
      ...hostActions,
      acceptWithEdits: (id, editedHtml) =>
        hostActions.acceptWithEdits(id, effective.compose(editedHtml)),
      persistEdited: (id, editedHtml) =>
        hostActions.persistEdited(id, effective.compose(editedHtml)),
    }
    return {
      registry,
      actions,
      originalHtml: original.additionHtml,
      editedContent:
        proposal.editedContent === undefined
          ? undefined
          : effective.additionHtml,
    }
  }, [fill, islandRegistry, hostActions, proposal.editedContent])

  const previewProposal = useMemo(
    () =>
      fill
        ? {
            ...proposal,
            htmlContent: fill.effective.additionHtml,
            editedContent: undefined,
          }
        : proposal,
    [fill, proposal]
  )

  /* Both branches are wrapped, not just the island: the preview renders the
   * same real node views, so anything that would write FHIR from a render or an
   * interaction is suppressed for a pending suggestion either way. Wrapping
   * here rather than inside each branch also means a future third widget state
   * inherits the suppression instead of having to remember it. */
  return (
    <RenderSafeContext.Provider value={true}>
      {/* The tint marks the text an accept would write, so it stops at the
          proposal: the shell around this holds the excerpt as well, and only
          this element is the card. */}
      <div className={AI_PROPOSAL_INSERT_CLASS}>
        {editing && island ? (
          <IslandActionsProvider value={island.actions}>
            <IslandEditor
              editedContent={island.editedContent}
              id={proposal.id}
              initialCaretPos={island.registry.getCaretPos(proposal.id)}
              originalHtml={island.originalHtml}
              registry={island.registry}
            />
          </IslandActionsProvider>
        ) : editing && islandRegistry ? (
          <IslandEditor
            id={proposal.id}
            originalHtml={suggestionContentHtml(
              proposal.render,
              proposal.htmlContent
            )}
            editedContent={proposal.editedContent}
            registry={islandRegistry}
            initialCaretPos={islandRegistry.getCaretPos(proposal.id)}
          />
        ) : (
          <SuggestionContentPreview
            proposal={previewProposal}
            onRequestEdit={
              islandRegistry
                ? (caretPos) =>
                    islandRegistry.open(proposal.id, caretPos ?? undefined)
                : undefined
            }
          />
        )}
      </div>
      {/* Under the card and OUTSIDE it, as the care-plan cards render it: an
       * accept drops the excerpt, so inside the tint it read as text about to
       * be written and its chip's border sat on the card's. Outside the
       * preview too — the preview's click-to-edit is bound to its own subtree,
       * so folding the disclosure must not read as a click on the suggestion
       * and open the island. */}
      <SuggestionSourceExcerpt
        citation={proposal.citation}
        sourceProvenance={proposal.sourceProvenance}
      />
    </RenderSafeContext.Provider>
  )
}

/* The AI's replacement and the one an accept would write — the clinician's
 * edit when they kept one — each split against the text the proposal replaces,
 * or null when either does not split: then the whole replacement shows, as for
 * any replace. Read against the live document at the proposal's current
 * position, which the plugin holds; the prop's own may have been mapped since. */
function useFillReplacement(
  editor: Editor | undefined,
  proposal: AIProposal
): { original: FillReplacement; effective: FillReplacement } | null {
  return useMemo(() => {
    if (!editor || !proposal.fillOnly || proposal.kind !== 'replace') {
      return null
    }
    const live =
      AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)?.proposals.get(
        proposal.id
      ) ?? proposal
    const { doc } = editor.state
    const original = splitFillReplacement(
      doc,
      live.from,
      live.to,
      proposal.htmlContent
    )
    const effective =
      proposal.editedContent === undefined
        ? original
        : splitFillReplacement(doc, live.from, live.to, proposal.editedContent)
    return original && effective ? { original, effective } : null
  }, [editor, proposal])
}
